import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthCard from '../../components/auth/AuthCard';
import BrandsLogin from '../../components/auth/BrandsLogin';
import CheckEmail from '../../components/auth/CheckEmail';
import KitInput from '../../components/UI/KitInput';
import { base64Decode } from '../../helpers/decodeToken';
import { AuthService } from '../../service/AuthService';
import { LocalStorageService } from '../../service/LocalStorageService';
import { useAuthStore } from '../../store/storeAuth';
import {
  useNotificationStore
} from '../../store/storeNotifications';
import { setPendingConfirmSameBrowser } from './ConfirmAccount';
import './Login.scss';

type AuthMode = 'email' | 'login' | 'register';

type IFormInput = {
  email: string;
  password: string;
  confirmPassword?: string;
  name?: string;
  termsAndConditions?: boolean;
};
const Login = () => {
  const navigate = useNavigate();
  const onNavigatePath = (path: string) => {
    navigate(`/${path}`);
  };
  const { setNotification } = useNotificationStore(state => state);

  const [authMode, setAuthMode] = useState<AuthMode>('email');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [showResendEmailVerification, setShowResendEmailVerification] =
    useState(false);
  const [registrationCompleted, setRegistrationCompleted] = useState(false);
  const [emailToResend, setEmailToResend] = useState('');

  const [tokenData, setTokenData] = useState<{
    token: string;
    decoded: {
      email: string;
    };
  }>({
    token: '',
    decoded: {
      email: '',
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    control,
    clearErrors,
    formState: { errors },
  } = useForm<IFormInput>();

  const { setAuthUser, setAccessToken } = useAuthStore(state => state);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setTokenData({
        token: token!,
        decoded: base64Decode(token!),
      });
      setValue('email', base64Decode(token!).email);
    }
  }, []);

  const location = useLocation();

  const onCheckEmail = async (values: IFormInput) => {
    if (!values.email) {
      setError('email', {
        type: 'validate',
        message: 'Email is required',
      });
      return;
    }

    setIsCheckingEmail(true);
    try {
      const response = await AuthService.checkEmail(values.email);
      const exists = response?.data?.exists;
      if (exists) {
        setAuthMode('login');
      } else {
        setAuthMode('register');
      }
    } catch (error: any) {
      setNotification({
        type: 'error',
        message: error.message || 'Failed to check email',
      });
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleSwitchToRegister = () => {
    clearErrors();
    setAuthMode('register');
    setValue('password', '');
    setValue('confirmPassword', '');
    setShowResendEmailVerification(false);
  };

  const handleSwitchToLogin = () => {
    clearErrors();
    setAuthMode('login');
    setValue('password', '');
    setValue('confirmPassword', '');
    setValue('name', '');
    setShowResendEmailVerification(false);
  };

  const resendEmailVerification = async (values: IFormInput) => {
    try {
      const response = await AuthService.resendVerificationEmail({
        email: values.email,
      });

      setNotification({
        type: 'success',
        message: response?.data?.message ?? 'Email sent',
      });
    } catch (error: any) {
      setNotification({
        type: 'error',
        message: error.message,
      });
    }
  };

  const resendRegistrationVerificationEmail = async () => {
    try {
      const response = await AuthService.resendVerificationEmail({
        email: emailToResend,
      });

      setNotification({
        type: 'success',
        message: response?.data?.message ?? 'Email sent',
      });
    } catch (error: any) {
      setNotification({
        type: 'error',
        message: error.message,
      });
    }
  };

  const onSubmitLogin = async (values: IFormInput) => {
    try {
      const response = await AuthService.login(values.email, values.password);

      setAuthUser(response.data);
      LocalStorageService.setItem('accessToken', response.data.accessToken);
      setAccessToken(response.data.accessToken);

      const urlParams = new URLSearchParams(location.search).get('token');

      if (urlParams) {
        setTimeout(() => {
          navigate('/company/accept-invite?token=' + urlParams);
        }, 1000);
      } else {
        navigate('/');
      }
    } catch (error: any) {
      if (error.message === 'Password is incorrect!') {
        setError('password', {
          type: 'validate',
          message: error.message,
        });
        setNotification({
          type: 'error',
          message: error.message,
        });
      } else if (error.message === 'Email does not exist!') {
        const message = 'Wrong username or password.';
        setError('email', {
          type: 'validate',
          message,
        });

        setNotification({
          type: 'error',
          message,
        });
      } else if (error.message === 'Please confirm your account!') {
        setShowResendEmailVerification(true);

        setNotification({
          type: 'error',
          message: error.message,
        });
      } else {
        setNotification({
          type: 'error',
          message: error.message || 'Something went wrong',
        });
      }
    }
  };

  const onSubmitRegister = async (values: IFormInput) => {
    const terms = watch('termsAndConditions');

    if (!terms) {
      setError('termsAndConditions', {
        type: 'validate',
        message: 'You must agree to the terms and conditions',
      });
      return;
    }

    try {
      const userData = await AuthService.register(values, tokenData.token);

      if (tokenData.token) {
        setAuthUser(userData.data);
        LocalStorageService.setItem('accessToken', userData.data.accessToken);
        setAccessToken(userData.data.accessToken);
        navigate('/');
      } else {
        setEmailToResend(values.email);
        setRegistrationCompleted(true);
        setPendingConfirmSameBrowser();
        setNotification({
          type: 'success',
          message: 'User registered successfully',
        });
      }
    } catch (error: any) {
      if (error.message === 'User already exist!') {
        setError('email', {
          type: 'validate',
          message: 'Email already exists',
        });
        setNotification({
          type: 'error',
          message: 'Email already exists',
        });
      } else {
        setNotification({
          type: 'error',
          message: error.message || 'Something went wrong',
        });
      }
    }
  };

  const onSubmit = (values: IFormInput) => {
    if (authMode === 'email') {
      onCheckEmail(values);
    } else if (authMode === 'login') {
      onSubmitLogin(values);
    } else if (authMode === 'register') {
      onSubmitRegister(values);
    }
  };

  const getHeaderText = () => {
    if (authMode === 'email') return 'Welcome';
    if (authMode === 'login') return 'Login';
    return 'Create Account';
  };

  return (
    <Box
      className=" login"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <AuthCard>
        {registrationCompleted ? (
          <CheckEmail
            bodyText="We have sent you an email with a link to verify your account. Please
          check your email and click on the link to verify your account."
            onNavigate={() => navigate('/')}
            resendEmailVerification={resendRegistrationVerificationEmail}
          />
        ) : (
          <>
            <form className="login_form" onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: authMode === 'email' ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: authMode === 'email' ? 'flex-start' : 'baseline',
              }}
              className="login_form_info"
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <h4 className="login_form_info--text">{getHeaderText()}</h4>
                {authMode === 'email' && (
                  <Typography
                    className="login_form_info--subtext"
                    sx={{ mt: '4px' }}
                  >
                    Enter your email to continue
                  </Typography>
                )}
              </Box>
              {authMode === 'login' && (
                <Typography
                  component="span"
                  className="subtext"
                  role="button"
                  sx={{
                    cursor: 'pointer',
                    textAlign: 'right',
                    color: '#3C5A99',
                  }}
                  onClick={handleSwitchToRegister}
                >
                  Create Account
                </Typography>
              )}
              {authMode === 'register' && (
                <Typography
                  component="span"
                  className="subtext"
                  role="button"
                  sx={{
                    cursor: 'pointer',
                    textAlign: 'right',
                    color: '#3C5A99',
                  }}
                  onClick={handleSwitchToLogin}
                >
                  Sign In Instead
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: '20px' }}>
              <KitInput
                register={register}
                name="email"
                label="Email"
                sxStyle={{
                  marginBottom: '20px',
                }}
                type="email"
                validation={{
                  required: {
                    value: true,
                    message: 'Email is required',
                  },
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                }}
                errorsStatus={errors}
              />

              {authMode === 'login' && (
                <Box className="auth-fields-transition">
                  <KitInput
                    name={'password'}
                    type="password"
                    register={register}
                    label="Password"
                    navigateText="Forgot Password?"
                    navigateTextSx={{ color: '#3C5A99' }}
                    onNavigate={() => onNavigatePath('forgot-password')}
                    errorsStatus={errors}
                    displayEnd
                    sxStyle={{
                      width: '100%',
                      marginBottom: '20px',
                    }}
                    validation={{
                      required: {
                        value: true,
                        message: 'Password is required',
                      },
                    }}
                  />

                  <Box display={'flex'} gap={'10px'} alignItems={'center'}>
                    <Checkbox name="rememberMe" />
                    <Typography className="subtext">Remember me</Typography>
                  </Box>

                  {showResendEmailVerification && (
                    <Box display={'flex'} margin={'10px 0'} alignItems={'center'}>
                      <Button
                        onClick={handleSubmit(resendEmailVerification)}
                        variant="text"
                        sx={{
                          backgroundColor: 'transparent',
                          padding: '2px 4px',
                          fontSize: '15px',
                          color: '#696969',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: '#3f3f3f',
                          },
                        }}
                      >
                        Resend verification email
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {authMode === 'register' && (
                <Box className="auth-fields-transition">
                  <KitInput
                    register={register}
                    name="name"
                    fullWidth={true}
                    label="Name"
                    sxStyle={{
                      marginBottom: '20px',
                    }}
                    validation={{
                      required: {
                        value: true,
                        message: 'Name is required',
                      },
                    }}
                    errorsStatus={errors}
                  />

                  <KitInput
                    name={'password'}
                    type="password"
                    register={register}
                    label="Password"
                    sxStyle={{
                      marginBottom: '20px',
                    }}
                    displayEnd
                    errorsStatus={errors}
                    validation={{
                      required: {
                        value: true,
                        message: 'Password is required',
                      },
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters',
                      },
                      onChange: e => {
                        if (e.target?.value !== watch('confirmPassword')) {
                          setError('confirmPassword', {
                            type: 'validate',
                            message: 'The passwords do not match',
                          });
                        } else {
                          setError('confirmPassword', {
                            type: '',
                            message: '',
                          });
                        }
                      },
                      pattern: {
                        value:
                          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{6,}$/,
                        message:
                          'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&#)',
                      },
                    }}
                  />

                  <KitInput
                    name={'confirmPassword'}
                    type="password"
                    sxStyle={{
                      marginBottom: '20px',
                    }}
                    displayEnd={true}
                    label="Confirm Password"
                    register={register}
                    errorsStatus={errors}
                    validation={{
                      required: {
                        value: true,
                        message: 'Confirm Password is required',
                      },
                      onChange: e => {
                        if (e.target?.value !== watch('password')) {
                          setError('confirmPassword', {
                            type: 'validate',
                            message: 'The passwords do not match',
                          });
                        } else {
                          setError('confirmPassword', {
                            type: '',
                            message: '',
                          });
                        }
                      },
                      validate: value =>
                        value === watch('password') ||
                        'The passwords do not match',
                    }}
                  />

                  <Controller
                    name="termsAndConditions"
                    control={control}
                    rules={{
                      required: {
                        message: 'You must agree to the terms and conditions',
                        value: true,
                      },
                    }}
                    defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            color={
                              errors?.termsAndConditions?.message
                                ? 'error'
                                : 'primary'
                            }
                          />
                        }
                        label="I agree to the Terms and Conditions"
                        sx={{
                          color: errors?.termsAndConditions?.message
                            ? 'error.main'
                            : 'primary.main',
                        }}
                      />
                    )}
                  />

                  {errors?.termsAndConditions?.message && (
                    <Typography
                      sx={{
                        color: 'error.main',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        mt: '5px',
                      }}
                    >
                      {'Please accept our terms to register'}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            <Button
              variant="contained"
              type="submit"
              disabled={isCheckingEmail}
              sx={{
                width: '100%',
                mt: '20px',
                backgroundColor: '#3C5A99',
                color: '#fff',
                mb: '20px',
                '&:hover': {
                  backgroundColor: '#3C5A99',
                },
              }}
            >
              {isCheckingEmail
                ? 'Checking...'
                : authMode === 'email'
                ? 'Continue'
                : authMode === 'login'
                ? 'Login'
                : 'Create Account'}
            </Button>
          </form>

          {authMode === 'email' && <BrandsLogin text="Or continue with" />}
          {authMode === 'login' && (
            <Box sx={{ mt: '20px' }}>
              <BrandsLogin type="login" text="Or login with" />
            </Box>
          )}
          {authMode === 'register' && (
            <Box sx={{ mt: '20px' }}>
              <BrandsLogin type="register" text="Or register with" />
            </Box>
          )}
          </>
        )}
      </AuthCard>
    </Box>
  );
};

export default Login;
